import { useState } from "react";
import { findTrendReversalPoints, analyzeTradingSignal } from "../services/tradingAnalysis";
import { BACKEND_API_URL } from "../apiConfig";

export default function useStockChartDataModel() {
    const [state, setState] = useState({
        data: null,
        symbol: "",
        options: {},
        series: [],
        isLoading: false,
        lastUpdate: 0,
        annotations: [],
        tradingSignal: 'NEUTRAL',
        predictedBuyRange: null,  // {min: number, max: number}
        predictedSellRange: null,  // {min: number, max: number}
        currentPrice: null
    })
    const updateState = (updates) =>
        setState((prev) => ({ ...prev, ...updates }));

    const normalizeSymbol = (value) =>
        String(value || "").replace(/\.NS$/i, "").toUpperCase();

    const getApiPrice = (result, symbolWithSuffix) => {
        if (!result || !Array.isArray(result.data)) return null;
        const priceData = result.data.find(p => normalizeSymbol(p.symbol) === normalizeSymbol(symbolWithSuffix));
        return priceData?.price ?? null;
    };

    const getApiPriceError = (result, symbolWithSuffix) => {
        if (!result || !Array.isArray(result.data)) return result?.error || null;
        const priceData = result.data.find(p => normalizeSymbol(p.symbol) === normalizeSymbol(symbolWithSuffix));
        return priceData?.error || result.error || null;
    };

    const getStockChartData = async (symbol) => {
        if (!symbol) {
            return;
        }

        // Check if we already have data for this symbol and it's recent (within last 5 minutes)
        if (state.data &&
            state.symbol === symbol &&
            (new Date().getTime() - state.lastUpdate) < 5 * 60 * 1000) {
            const updatedData = changeApiData(state.data);
            showChartData(updatedData);
            return;
        }
        updateState({
            isLoading: true,
            series: [],
            options: {},
            symbol: symbol
        });

        const symbolWithSuffix = `${symbol}.NS`;
        let historyResult = null;
        let priceResult = null;
        let priceError = null;
        let historicalError = null;

        try {
            const [historyResponse, priceResponse] = await Promise.all([
                fetch(`${BACKEND_API_URL}/api/upstox/historical`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ symbols: [symbolWithSuffix] })
                }),
                fetch(`${BACKEND_API_URL}/api/upstox/prices`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ symbols: [symbolWithSuffix] })
                })
            ]);

            historyResult = await historyResponse.json();
            if (!historyResponse.ok) {
                historicalError = historyResult?.error || `Failed to fetch historical data for ${symbol}`;
            }

            priceResult = await priceResponse.json();
            if (!priceResponse.ok) {
                priceError = priceResult?.error || `Failed to fetch current price for ${symbol}`;
            }
        } catch (error) {
            console.error(`Request failed for ${symbol}:`, error.message);
            historicalError = historicalError || error.message;
            priceError = priceError || error.message;
        }

        const latestPrice = getApiPrice(priceResult, symbolWithSuffix);
        const availableHistory = Array.isArray(historyResult?.data) ? historyResult.data : [];

        if (latestPrice != null) {
            updateState({ currentPrice: latestPrice });
        } else if (priceResult) {
            const message = getApiPriceError(priceResult, symbolWithSuffix);
            priceError = priceError || message;
            updateState({ currentPrice: null });
        }

        if (availableHistory.length > 0) {
            updateState({
                data: historyResult,
                lastUpdate: new Date().getTime()
            });

            try {
                const updatedData = await changeApiData(historyResult);
                await showChartData(updatedData);
                return;
            } catch (error) {
                console.error(`Error processing historical chart data for ${symbol}:`, error.message);
                historicalError = historicalError || error.message;
            }
        }

        console.error(`Error processing ${symbol}: historicalError=${historicalError}, priceError=${priceError}`);
        updateState({
            isLoading: false,
            series: [],
            options: {},
            currentPrice: latestPrice ?? null
        });
    };
    
    const changeApiData = (dataArray) => {
        if (!Array.isArray(dataArray?.data) || dataArray.data.length === 0) {
            throw new Error('No historical data available for chart');
        }

        const timeSeries = {};
        dataArray.data.forEach(entry => {
            const date = entry.date.split("T")[0]; // Extract YYYY-MM-DD
            timeSeries[date] = {
                "1. open": entry.open ?? entry.low,   // If 'open' missing, fallback
                "2. high": entry.high,
                "3. low": entry.low,
                "4. close": entry.close ?? entry.high // If 'close' missing, fallback
            };
        });

        // Build Meta Data
        const metaData = {
            "1. Information": "Daily Prices (open, high, low, close) and Volumes",
            "2. Symbol": dataArray.data[0]?.symbol || state.symbol,
            "3. Last Refreshed": dataArray.data[0]?.date.split("T")[0] || "",
            "4. Output Size": "Full size",
            "5. Time Zone": "US/Eastern"
        };

        return {
            "Meta Data": metaData,
            "Time Series (Daily)": timeSeries
        };
    }

    const showChartData = async (apiData) => {

        try {
            if (!apiData || typeof apiData !== 'object') {
                updateState({ isLoading: false });
                return;
            }
            if (!apiData['Time Series (Daily)']) {
                updateState({ isLoading: false });
                return;
            }

            const timeSeries = apiData['Time Series (Daily)'];
            const symbol = apiData['Meta Data']['2. Symbol'];

            // Set date range from current date to 2 years ago
            const endDate = new Date(); // Current date
            const startDate = new Date();
            startDate.setFullYear(endDate.getFullYear() - 3);

            // Process data for the date range

            // Constants for pattern detection
            const PATTERN_CONSTANTS = {
                priceChangeThreshold: 0.02, // 2% minimum price change for patterns
                minimumPriceDistance: 0.03, // 3% minimum distance between marked points
                patternLookback: 7, // Days to look before and after for patterns
                roundedShapeThreshold: 0.005 // 0.5% variation for rounded patterns
            };

            // Process and validate the data
            let filteredData = Object.entries(timeSeries)
                .filter(([date]) => {
                    const dateObj = new Date(date);
                    return dateObj >= startDate && dateObj <= endDate;
                })
                .map(([date, values]) => {
                    // Ensure all required values are present and valid
                    const open = parseFloat(values['1. open']);
                    const high = parseFloat(values['2. high']);
                    const low = parseFloat(values['3. low']);
                    const close = parseFloat(values['4. close']);

                    // Validate that all values are numbers and make sense
                    if (isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close)) {
                        return null;
                    }

                    // Validate price relationships
                    if (high < low || high < open || high < close || low > open || low > close) {
                        return null;
                    }

                    return {
                        timestamp: new Date(date).getTime(),
                        open,
                        high,
                        low,
                        close
                    };
                })
                .filter(item => item !== null)
                .sort((a, b) => a.timestamp - b.timestamp);

            // Find triangle/inverse-V shaped top points
            const highPoints = [];
            // Calculate date threshold for 1 year ago
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

            for (let i = PATTERN_CONSTANTS.patternLookback; i < filteredData.length - PATTERN_CONSTANTS.patternLookback; i++) {
                // Only process points from the last year
                const pointDate = new Date(filteredData[i].timestamp);
                if (pointDate < oneYearAgo) continue;
                const current = filteredData[i];
                let isSignificantTop = false;
                let hasNearbyHigherPoint = false;

                // Check if this point is a local maximum within a wider range
                for (let j = i - PATTERN_CONSTANTS.patternLookback; j <= i + PATTERN_CONSTANTS.patternLookback; j++) {
                    if (j !== i && filteredData[j].high > current.high) {
                        hasNearbyHigherPoint = true;
                        break;
                    }
                }

                if (!hasNearbyHigherPoint) {
                    // Check for triangle top with stronger movement requirements

                    // Calculate the highest and lowest points before and after
                    let lowestBefore = current.high;
                    let lowestAfter = current.high;

                    // Find lowest point before the top (for rise calculation)
                    for (let j = i - PATTERN_CONSTANTS.patternLookback; j < i; j++) {
                        if (filteredData[j].high < lowestBefore) {
                            lowestBefore = filteredData[j].high;
                        }
                    }

                    // Find lowest point after the top (for fall calculation)
                    for (let j = i + 1; j <= i + PATTERN_CONSTANTS.patternLookback; j++) {
                        if (filteredData[j].high < lowestAfter) {
                            lowestAfter = filteredData[j].high;
                        }
                    }

                    // Calculate percentage changes
                    const risePercent = (current.high - lowestBefore) / lowestBefore;
                    const fallPercent = (current.high - lowestAfter) / current.high;

                    // Check for significant triangle top pattern
                    const isTrianglePattern = risePercent > PATTERN_CONSTANTS.priceChangeThreshold && fallPercent > PATTERN_CONSTANTS.priceChangeThreshold;

                    // Check for rounded top with stricter criteria
                    let isRoundedTop = false;
                    if (!isTrianglePattern) {  // Only check for rounded if not a triangle
                        let steadyPriceCount = 0;
                        const roundedTopThreshold = current.high * PATTERN_CONSTANTS.roundedShapeThreshold;

                        // Count how many days price stays near the top
                        for (let j = i - 2; j <= i + 2; j++) {
                            if (Math.abs(filteredData[j].high - current.high) <= roundedTopThreshold) {
                                steadyPriceCount++;
                            }
                        }

                        // Require at least 3 days of steady price and fall after
                        isRoundedTop = steadyPriceCount >= 3 && fallPercent > PATTERN_CONSTANTS.priceChangeThreshold;
                    }

                    isSignificantTop = isTrianglePattern || isRoundedTop;

                    if (isSignificantTop) {
                        // Check for minimum price distance from existing points
                        const tooCloseToExisting = highPoints.some(point =>
                            Math.abs(point.price - current.high) / current.high < PATTERN_CONSTANTS.minimumPriceDistance
                        );

                        if (!tooCloseToExisting) {
                            highPoints.push({
                                price: current.high,
                                index: i
                            });
                        }
                    }
                }
            }

            // Keep all high points, no slicing
            const significantHighs = highPoints;

            // Find V-shaped bottom points
            const lowPoints = [];
            // Using shared constants for pattern detection
            // Note: oneYearAgo is already defined above

            for (let i = PATTERN_CONSTANTS.patternLookback; i < filteredData.length - PATTERN_CONSTANTS.patternLookback; i++) {
                // Only process points from the last year
                const pointDate = new Date(filteredData[i].timestamp);
                if (pointDate < oneYearAgo) continue;
                const current = filteredData[i];
                let isSignificantBottom = false;
                let hasNearbyLowerPoint = false;

                // Check if this point is a local minimum within a wider range
                for (let j = i - PATTERN_CONSTANTS.patternLookback; j <= i + PATTERN_CONSTANTS.patternLookback; j++) {
                    if (j !== i && filteredData[j].low < current.low) {
                        hasNearbyLowerPoint = true;
                        break;
                    }
                }

                if (!hasNearbyLowerPoint) {
                    // Check for V shape with stronger movement requirements

                    // Calculate the lowest price before and highest price after
                    let lowestBefore = current.low;
                    let highestAfter = current.low;

                    // Find highest point before the bottom (for decline calculation)
                    for (let j = i - PATTERN_CONSTANTS.patternLookback; j < i; j++) {
                        if (filteredData[j].low > lowestBefore) {
                            lowestBefore = filteredData[j].low;
                        }
                    }

                    // Find highest point after the bottom (for rise calculation)
                    for (let j = i + 1; j <= i + PATTERN_CONSTANTS.patternLookback; j++) {
                        if (filteredData[j].low > highestAfter) {
                            highestAfter = filteredData[j].low;
                        }
                    }

                    // Calculate percentage changes
                    const declinePercent = (lowestBefore - current.low) / lowestBefore;
                    const risePercent = (highestAfter - current.low) / current.low;

                    // Check for significant V pattern
                    const isVPattern = declinePercent > PATTERN_CONSTANTS.priceChangeThreshold && risePercent > PATTERN_CONSTANTS.priceChangeThreshold;

                    // Check for U pattern with stricter criteria
                    let isUPattern = false;
                    if (!isVPattern) {  // Only check for U if not a V
                        let steadyPriceCount = 0;
                        const uShapeThreshold = current.low * 0.005; // 0.5% variation for U shape

                        // Count how many days price stays near the bottom
                        for (let j = i - 2; j <= i + 2; j++) {
                            if (Math.abs(filteredData[j].low - current.low) <= uShapeThreshold) {
                                steadyPriceCount++;
                            }
                        }

                        // Require at least 3 days of steady price and rise after
                        isUPattern = steadyPriceCount >= 3 && risePercent > PATTERN_CONSTANTS.priceChangeThreshold;
                    }

                    isSignificantBottom = isVPattern || isUPattern;

                    if (isSignificantBottom) {
                        // Check for minimum price distance from existing points
                        const tooCloseToExisting = lowPoints.some(point =>
                            Math.abs(point.price - current.low) / current.low < PATTERN_CONSTANTS.minimumPriceDistance
                        );

                        if (!tooCloseToExisting) {
                            lowPoints.push({
                                price: current.low,
                                index: i
                            });
                        }
                    }
                }
            }

            // Keep all low points, no slicing
            const significantLows = lowPoints;


           
            const seriesData = filteredData.reduce((acc, current, index) => {
                let haOpen, haClose, haHigh, haLow;

                
                haClose = (current.open + current.high + current.low + current.close) / 4;

                if (index === 0) {
                    haOpen = current.open;
                } else {
                    const prevHA = acc[acc.length - 1].y;
                    haOpen = (prevHA[0] + prevHA[3]) / 2; // Previous HA open + close / 2
                }

                haHigh = Math.max(current.high, haOpen, haClose);
                haLow = Math.min(current.low, haOpen, haClose);

                // Use continuous numeric index for x-axis to prevent gaps
                acc.push({
                    x: index,
                    y: [haOpen, haHigh, haLow, haClose],
                    realDate: current.timestamp // Store actual date for tooltip
                });

                return acc;
            }, []);

            // Add extra month of dates for panning
            const lastDataPoint = filteredData[filteredData.length - 1];
            const lastDate = new Date(lastDataPoint.timestamp);
            const extraDates = 30; // Add 30 more days

            for (let i = 1; i <= extraDates; i++) {
                const nextDate = new Date(lastDate);
                nextDate.setDate(lastDate.getDate() + i);
                seriesData.push({
                    x: seriesData.length,
                    y: [null, null, null, null], // Empty candle for future dates
                    realDate: nextDate.getTime()
                });
            }

            if (seriesData.length === 0) {
                updateState({
                    isLoading: false,
                    series: [],
                    options: {}
                });
                return;
            }

            // Fetch current price from the API
            let currentPrice;
            try {
    // The backend expects symbols with the '.NS' suffix.
    const symbolWithSuffix = `${symbol}`;

     const response = await fetch(`${BACKEND_API_URL}/api/upstox/prices`, {
         method: 'POST',
         headers: {
             'Content-Type': 'application/json',
         },
         // Send the symbol in the request body as an array
         body: JSON.stringify({ symbols: [symbolWithSuffix] })
     });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    // The backend returns an array of price objects. Find the one for our symbol.
    const priceData = result.data.find(p => p.symbol === symbolWithSuffix);

    if (!priceData || priceData.price == null) {
        throw new Error(`Could not get price from response for ${symbol}`);
    }

    // Assign the price to your variable
    currentPrice = priceData.price;

} catch (error) {
    console.error('Error fetching current price:', error.message);
}


            // Analyze trading signal and get predicted ranges
            let signal = 'NEUTRAL', predictedBuyRange = null, predictedSellRange = null;
            if (currentPrice) {
                const analysis = analyzeTradingSignal(filteredData, currentPrice, symbol);
                signal = analysis.signal;
                predictedBuyRange = analysis.predictedBuyRange;
                predictedSellRange = analysis.predictedSellRange;
                // console.log(`Chart Analysis for ${symbol}:`, { predictedBuyRange, predictedSellRange });
            }

            const daysToShow = 60;
            const lastDataIndex = filteredData.length - 1;
            const initialMin = Math.max(0, lastDataIndex - daysToShow);
            const initialMax = lastDataIndex + 30; // Keep the extra space for panning

            // Update state with the processed data
            updateState({
                isLoading: false,
                tradingSignal: signal,
                predictedBuyRange,
                predictedSellRange,
                series: [{
                    name: 'OHLC',
                    data: seriesData
                }],
                options: {
                    chart: {
                        type: 'candlestick',
                        height: 400,
                        animations: {
                            enabled: false  // Disable animations for more accurate representation
                        },
                        events: {
                            click: function (event, chartContext, config) {
                                if (config.dataPointIndex >= 0 && typeof config.y === 'number') {
                                    const y = config.y;

                                    // Create price label without line
                                    const newAnnotation = {
                                        y: y,
                                        borderColor: '#00E396',
                                        strokeDashArray: 0,
                                        borderWidth: 1,
                                        x: config.dataPointIndex - 2, // Start 2 points before click
                                        x2: config.dataPointIndex + 2, // End 2 points after click
                                        label: {
                                            text: `${y.toFixed(2)}`,
                                            style: {
                                                color: '#fff',
                                                background: '#00E396',
                                                fontSize: '11px'
                                            },
                                            position: 'right',
                                            offsetX: 5
                                        }
                                    };

                                    const currentOptions = chartContext.w.config.options;
                                    const currentAnnotations = currentOptions.annotations?.yaxis || [];

                                    currentOptions.annotations = {
                                        ...currentOptions.annotations,
                                        yaxis: [...currentAnnotations, newAnnotation]
                                    };

                                    chartContext.updateOptions(currentOptions);
                                }
                            }
                        },
                        toolbar: {
                            show: true,
                            tools: {
                                download: true,
                                selection: true,
                                zoom: true,
                                zoomin: true,
                                zoomout: true,
                                pan: true,
                                reset: true
                            }
                        },
                        zoom: {
                            enabled: true,
                            type: 'xy',
                            autoScaleYaxis: true
                        }
                    },
                    plotOptions: {
                        candlestick: {
                            colors: {
                                upward: '#00B200',  // Green for up moves
                                downward: '#FF0000'  // Red for down moves
                            },
                            wick: {
                                useFillColor: true,
                            }
                        }
                    },
                    grid: {
                        show: true,
                        borderColor: '#90A4AE',
                        strokeDashArray: 0,
                        position: 'back'
                    },
                    annotations: {
                        yaxis: [
                            // Full-width resistance lines in yellow
                            ...significantHighs.map(point => ({
                                y: point.price,
                                borderColor: '#FFFF00',
                                strokeDashArray: 0,
                                borderWidth: 1,
                                x: Math.max(0, filteredData.findIndex(d => new Date(d.timestamp) >= oneYearAgo)),
                                x2: filteredData.length + 30,
                                label: {
                                    text: point.price.toFixed(2),
                                    style: {
                                        background: '#FFFF00',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        cssClass: 'resistance-label'
                                    },
                                    position: 'right',
                                    offsetX: 5
                                }
                            })),
                            // Full-width support lines in red
                            ...significantLows.map(point => ({
                                y: point.price,
                                borderColor: '#FF0000',
                                strokeDashArray: 0,
                                borderWidth: 1,
                                x: Math.max(0, filteredData.findIndex(d => new Date(d.timestamp) >= oneYearAgo)),
                                x2: filteredData.length + 30,
                                label: {
                                    text: point.price.toFixed(2),
                                    style: {
                                        color: '#FFFFFF',  // White text for red background
                                        background: '#FF0000',
                                        fontSize: '11px',
                                        fontWeight: 'bold'
                                    },
                                    position: 'right',
                                    offsetX: 5
                                }
                            }))],
                        points: [
                            // Modern "BUY" and "SELL" labels
                            ...findTrendReversalPoints(filteredData).map(p => {
                                const isTop = p.isTop;
                                const sellColor = '#b91c1c'; // Dark Red
                                const buyColor = '#16a34a';  // Dark Green

                                const labelStyle = {
                                    background: isTop ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)', // Light filled boxes
                                    color: '#ffffff', // White text
                                    fontSize: "12px",
                                    fontWeight: 600,
                                };

                                return {
                                    x: p.index,
                                    y: p.price,
                                    marker: {
                                        size: 0, // Hide the default dot marker
                                    },
                                    label: {
                                        borderColor: isTop ? sellColor : buyColor, // Dark border
                                        borderWidth: 1,
                                        offsetY: isTop ? -15 : 15, // Position SELL above, BUY below
                                        style: labelStyle,
                                        text: isTop ? `SELL @ ${p.price.toFixed(2)}` : `BUY @ ${p.price.toFixed(2)}`,
                                    },
                                    // Show tooltip on hover
                                    mouseEnter: function (e, chart, config) {
                                        // Find the original data point index from the annotation point
                                        const pointIndex = config.globals.points.findIndex(point => point.x === p.index && point.y === p.price);
                                        if (pointIndex > -1) {
                                            config.w.globals.tooltip.markerColors = [isTop ? "#ef4444" : "#22c55e"];
                                            config.w.globals.tooltip.showTooltip({
                                                seriesIndex: 0,
                                                dataPointIndex: p.index,
                                                w: config.w,
                                            });
                                        }
                                    },
                                };
                            })
                        ],
                    },
                    title: {
                        text: `${symbol} Stock Price (Last 3 Years)`,
                        align: 'left',
                        style: {
                            fontSize: '14px',
                            color: '#ffffff'
                        }
                    },
                    xaxis: {
                        type: 'numeric',
                        labels: {
                            style: {
                                fontSize: '10px',
                                colors: '#ffffff'
                            },
                            formatter: function (value) {
                                const dataPoint = seriesData[Math.floor(value)];
                                if (dataPoint) {
                                    const date = new Date(dataPoint.realDate);
                                    return date.toLocaleDateString('en-IN', {
                                        day: 'numeric',
                                        month: 'short'
                                    });
                                }
                                return '';
                            },
                            rotate: -45,
                            rotateAlways: true
                        },
                        axisBorder: {
                            show: true,
                            color: '#78909C'
                        },
                        axisTicks: {
                            show: true
                        },
                        tickAmount: 15, // Increased number of ticks
                        min: initialMin,
                        max: initialMax,
                        tooltip: {
                            enabled: true,
                            formatter: function (value) {
                                const dataPoint = seriesData[Math.floor(value)];
                                return dataPoint ? new Date(dataPoint.realDate).toLocaleDateString() : '';
                            }
                        }
                    },
                    yaxis: {
                        decimalsInFloat: 2,
                        floating: false,
                        forceNiceScale: true,
                        labels: {
                            show: true,
                            formatter: function (value) {
                                // Check if value is null or undefined
                                if (value === null || value === undefined) {
                                    return '';
                                }
                                // Format number with 3 decimal places
                                return value.toFixed(3);
                            },
                            style: {
                                fontSize: '11px',
                                fontFamily: 'Consolas, monospace',
                                cssClass: 'apexcharts-yaxis-label',
                                colors: '#ffffff'
                            },
                            align: 'right',
                            offsetX: -10,
                            minWidth: 100,
                            maxWidth: 160
                        },
                        axisBorder: {
                            show: true,
                            color: '#78909C',
                            offsetX: -5
                        },
                        axisTicks: {
                            show: true,
                            offsetX: -5
                        },
                        crosshairs: {
                            show: true,
                            position: 'back',
                            stroke: {
                                color: '#b6b6b6',
                                width: 1,
                                dashArray: 0
                            }
                        },
                        tickAmount: 8,
                        opposite: false,
                        tooltip: {
                            enabled: true
                        },
                        margin: 20
                    },
                    tooltip: {
                        enabled: true,
                        theme: 'dark',
                        fixed: {
                            enabled: true,
                            position: 'topRight',
                            offsetX: 0,
                            offsetY: 0
                        },
                        custom: function ({ seriesIndex, dataPointIndex, w }) {
                            const date = new Date(seriesData[dataPointIndex].realDate);

                            // Check if we have valid OHLC data for this point
                            const o = w.globals.seriesCandleO[seriesIndex][dataPointIndex];
                            const h = w.globals.seriesCandleH[seriesIndex][dataPointIndex];
                            const l = w.globals.seriesCandleL[seriesIndex][dataPointIndex];
                            const c = w.globals.seriesCandleC[seriesIndex][dataPointIndex];

                            // If any of the OHLC values are null, show only the date
                            if (o === null || h === null || l === null || c === null) {
                                return (
                                    '<div class="apexcharts-tooltip-box" style="background: rgba(30, 30, 30, 0.9); padding: 6px; border-radius: 4px; border: 1px solid #333; max-width: 150px;">' +
                                    '<div style="font-size: 11px; font-weight: bold; color: #FFD700;">' +
                                    date.toLocaleDateString('en-IN', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                    }) +
                                    '</div>' +
                                    '<div style="color: #fff; font-size: 11px;">Future date (No data)</div>' +
                                    '</div>'
                                );
                            }

                            // For points with data, show full OHLC information
                            return (
                                '<div class="apexcharts-tooltip-box" style="background: rgba(30, 30, 30, 0.9); padding: 6px; border-radius: 4px; border: 1px solid #333; max-width: 150px;">' +
                                '<div style="font-size: 11px; font-weight: bold; margin-bottom: 4px; color: #FFD700;">' +
                                date.toLocaleDateString('en-IN', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                }) +
                                '</div>' +
                                '<div style="color: #fff; font-size: 11px; line-height: 1.4;">' +
                                `<div style="display: flex; justify-content: space-between;"><span>O:</span><span>₹${o.toFixed(3)}</span></div>` +
                                `<div style="display: flex; justify-content: space-between;"><span>H:</span><span>₹${h.toFixed(3)}</span></div>` +
                                `<div style="display: flex; justify-content: space-between;"><span>L:</span><span>₹${l.toFixed(3)}</span></div>` +
                                `<div style="display: flex; justify-content: space-between;"><span>C:</span><span>₹${c.toFixed(3)}</span></div>` +
                                '</div>' +
                                '</div>'
                            );
                        },
                        onDatasetHover: {
                            highlightDataSeries: true
                        },
                        x: {
                            show: true,
                            format: 'dd MMM yyyy'
                        }
                    }
                }
            });
        } catch (error) {
            updateState({
                isLoading: false,
                series: [],
                options: {}
            });
        }
    };

    return {
        state,
        updateState,
        getStockChartData,
        showChartData
    };
}
